import {Dispatch} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import {
    finishAssistantMessage,
    updateThread,
    replaceMessage,
    addSourceToLastMessage,
    updateThreadName,
    setRealThreadId,
    rateLimitReset,
    addAgentThought,
    addAgentActivity,
    addNarration,
    addPluginActivity,
    addRelatedToolsToLastMessage,
    addArtifactToLastMessage,
    handleStreamedArtifact,
    addLowScoreMessage,
    addNoAnswerMessage,
    addNoSourcesMessage,
    addStreamError,
    setPostSearchFromStream,
    setPostSearchPending,
    setFollowUpSuggestionsOnLastMessage,
} from "./threadsSlice";
import {ChatStopReason, MessageEnum, StreamErrorType} from "../../types";

export interface MessageHandlerContext {
    dispatch: Dispatch;
    accumulatedText: string;
    accumulatedDispatched: boolean;
    delta: Date;
    streamId: string;
    onThreadIdReceived?: (threadId: string) => void;
}

export type MessageHandlerResult = {
    accumulatedText?: string;
    delta?: Date;
    accumulatedDispatched?: boolean;
    // Set by the handler that finalizes the turn, so the stream loop can report
    // how the turn ended.
    stopReason?: ChatStopReason;
};

export type MessageHandler = (
    parsed: any,
    context: MessageHandlerContext
) => MessageHandlerResult;

const handleThreadCreated: MessageHandler = (
    parsed,
    {dispatch, streamId, onThreadIdReceived}
) => {
    console.log("[multi-stream] ThreadCreated", {streamId, realThreadId: parsed.threadId, hasCallback: !!onThreadIdReceived});
    dispatch(
        setRealThreadId({id: streamId, realThreadId: parsed.threadId})
    );
    onThreadIdReceived?.(parsed.threadId);
    return {};
};

const handleThreadMetaMessage: MessageHandler = (
    parsed,
    {dispatch, streamId, onThreadIdReceived}
) => {
    dispatch(
        updateThread({
            id: streamId,
            realThreadId: parsed.threadId,
            name: parsed.name,
            messages: parsed.messages,
            datestamp: parsed.datestamp,
        })
    );
    onThreadIdReceived?.(parsed.threadId);
    return {};
};

const handleThreadNameMessage: MessageHandler = (
    parsed,
    {dispatch, streamId}
) => {
    dispatch(
        updateThreadName({
            id: streamId,
            name: parsed.threadName,
        })
    );
    return {};
};

const handleMessageWithContent: MessageHandler = (
    parsed,
    {dispatch, streamId, accumulatedText, delta}
) => {
    const content = parsed.content;
    if (!content) {
        return {};
    }

    const newAccumulatedText = accumulatedText + content;
    const timeSinceDelta = new Date().getTime() - delta.getTime();

    // Limit the amount of updates to the redux store
    if (timeSinceDelta > 100) {
        dispatch(
            replaceMessage({id: streamId, content: newAccumulatedText})
        );
        return {
            accumulatedText: newAccumulatedText,
            delta: new Date(),
        };
    }

    return {accumulatedText: newAccumulatedText};
};

const handleStopMessage: MessageHandler = (
    parsed,
    {dispatch, streamId, accumulatedText, accumulatedDispatched}
) => {
    // The backend also sends a stop frame after a terminal error. The error
    // handler finalized the message already. A second call overwrites the
    // content and sends a second report for the same turn.
    if (accumulatedDispatched) {
        return {};
    }
    const stopReason = parsed.stop_reason as ChatStopReason;
    dispatch(
        finishAssistantMessage({
            id: streamId,
            text: accumulatedText,
            stopReason,
        })
    );
    return {accumulatedDispatched: true, stopReason};
};

const handleErrorMessage: MessageHandler = (
    parsed,
    {dispatch, streamId, accumulatedText}
) => {
    dispatch(addStreamError({id: streamId, error: parsed}));
    if (parsed.errorType === StreamErrorType.RateLimitExceeded) {
        dispatch(rateLimitReset({id: streamId}));
        return {accumulatedText: ""};
    }
    // Terminal error — backend is about to send STREAMDONE and will not send a
    // StopMessage. Finalize the assistant message here so the UI leaves streaming
    // state instead of spinning forever.
    if (parsed.errorType === StreamErrorType.ContentFilter) {
        dispatch(
            finishAssistantMessage({
                id: streamId,
                text: accumulatedText,
                stopReason: ChatStopReason.ContentFilter,
            })
        );
        return {
            accumulatedDispatched: true,
            stopReason: ChatStopReason.ContentFilter,
        };
    }
    return {};
};

const handleRagSourceMessage: MessageHandler = (parsed, {dispatch, streamId}) => {
    dispatch(addSourceToLastMessage({id: streamId, payload: parsed}));
    return {};
};

const handleRelatedToolsMessage: MessageHandler = (
    parsed,
    {dispatch, streamId}
) => {
    dispatch(addRelatedToolsToLastMessage({id: streamId, payload: parsed}));
    return {};
};

const handleNoOp: MessageHandler = () => ({});

const handleAgentActivity: MessageHandler = (parsed, {dispatch, streamId}) => {
    dispatch(addAgentActivity({id: streamId, payload: parsed}));
    return {};
};

const handleAgentThought: MessageHandler = (parsed, {dispatch, streamId}) => {
    dispatch(addAgentThought({id: streamId, payload: parsed}));
    return {};
};

const handleNarration: MessageHandler = (parsed, {dispatch, streamId}) => {
    dispatch(addNarration({id: streamId, payload: parsed}));
    return {};
};

const handlePluginActivity: MessageHandler = (parsed, {dispatch, streamId}) => {
    dispatch(addPluginActivity({id: streamId, payload: parsed}));
    return {};
};

const handleLowScoreMessage: MessageHandler = (parsed, {dispatch, streamId}) => {
    dispatch(addLowScoreMessage({id: streamId, message: parsed}));
    return {};
};

const handleNoAnswerMessage: MessageHandler = (parsed, {dispatch, streamId}) => {
    dispatch(addNoAnswerMessage({id: streamId, message: parsed}));
    return {};
};

const handleNoSourcesMessage: MessageHandler = (
    parsed,
    {dispatch, streamId}
) => {
    dispatch(addNoSourcesMessage({id: streamId, message: parsed}));
    return {};
};

const handleFollowUpSuggestionsMessage: MessageHandler = (
    parsed,
    {dispatch, streamId}
) => {
    dispatch(
        setFollowUpSuggestionsOnLastMessage({
            id: streamId,
            suggestions: parsed.suggestions ?? [],
        })
    );
    return {};
};

const handleArtifactMessage: MessageHandler = (parsed, {dispatch, streamId}) => {
    dispatch(addArtifactToLastMessage({id: streamId, payload: parsed}));
    return {};
};

const handleStreamedArtifactMessage: MessageHandler = (
    parsed,
    {dispatch, streamId}
) => {
    dispatch(handleStreamedArtifact({id: streamId, payload: parsed}));
    return {};
};

// The `process`/postsearch frame signals the backend has started an in-stream
// PostSearch. It carries no query, so setPostSearchPending derives one from the
// thread's last user message. This is the only trigger for the searching state.
const handlePostSearchStartMessage: MessageHandler = (
    _parsed,
    {dispatch, streamId}
) => {
    dispatch(setPostSearchPending({id: streamId, streamId}));
    return {};
};

const handlePostSearchResultMessage: MessageHandler = (
    parsed,
    {dispatch, streamId}
) => {
    dispatch(
        setPostSearchFromStream({
            id: streamId,
            query: parsed.query ?? "",
            tools: parsed.tools ?? [],
            otherSources: parsed.otherSources ?? [],
        })
    );
    return {};
};

export const MESSAGE_HANDLERS: Record<MessageEnum, MessageHandler> = {
    [MessageEnum.ThreadCreated]: handleThreadCreated,
    [MessageEnum.ThreadMetaMessage]: handleThreadMetaMessage,
    [MessageEnum.ThreadNameMessage]: handleThreadNameMessage,
    [MessageEnum.ChatCompletionMessage]: handleMessageWithContent,
    [MessageEnum.MessageSegment]: handleMessageWithContent,
    [MessageEnum.StopMessage]: handleStopMessage,
    [MessageEnum.RagProcessMessage]: handleNoOp,
    [MessageEnum.FilteredContentMessage]: handleNoOp,
    [MessageEnum.ThinkingCompleteMessage]: handleNoOp,
    [MessageEnum.FollowUpMessage]: handleNoOp,
    [MessageEnum.UsageMessage]: handleNoOp,
    [MessageEnum.ErrorMessage]: handleErrorMessage,
    [MessageEnum.RagSourceMessage]: handleRagSourceMessage,
    [MessageEnum.RelatedToolsMessage]: handleRelatedToolsMessage,
    [MessageEnum.AgentActivity]: handleAgentActivity,
    [MessageEnum.AgentThought]: handleAgentThought,
    [MessageEnum.Narration]: handleNarration,
    [MessageEnum.PluginActivity]: handlePluginActivity,
    [MessageEnum.LowScoreMessage]: handleLowScoreMessage,
    [MessageEnum.NoAnswerMessage]: handleNoAnswerMessage,
    [MessageEnum.NoScoreMessage]: handleNoSourcesMessage,
    [MessageEnum.ArtifactMessage]: handleArtifactMessage,
    [MessageEnum.StreamedArtifactMessage]: handleStreamedArtifactMessage,
    [MessageEnum.PostSearchResultMessage]: handlePostSearchResultMessage,
    [MessageEnum.PostSearchStartMessage]: handlePostSearchStartMessage,
    [MessageEnum.FollowUpSuggestionsMessage]: handleFollowUpSuggestionsMessage,
};

export const handleMessage = (
    messageType: MessageEnum | null,
    parsed: any,
    context: MessageHandlerContext
): MessageHandlerResult => {
    if (messageType === null) {
        console.warn("Unknown message type received:", parsed);
        return {};
    }

    const handler = MESSAGE_HANDLERS[messageType];
    if (!handler) {
        console.warn(
            `No handler found for message type: ${MessageEnum[messageType]}`
        );
        return {};
    }

    try {
        return handler(parsed, context);
    } catch (error) {
        console.error(
            `Error handling message type ${MessageEnum[messageType]}:`,
            error,
            {keys: Object.keys(parsed ?? {})}
        );
        Sentry.captureException(error, {
            tags: {
                feature: "chat.message-handler",
                messageType: MessageEnum[messageType],
            },
        });
        return {};
    }
};
